import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useFilterSearchConfig } from '../../hooks/useFilterSearchConfig';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { FilterSearchInput } from '../CheckBoxGroup/FilterSearchInput';
import { FilterSearchNoResults } from '../CheckBoxGroup/FilterSearchNoResults';
import { FilterCollectionModel } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';
import { Distance } from '../ProviderDetailTabs/ProviderLocationsDetails/Distance';
import { DistanceInfoIcon } from './DistanceInfoIcon';
import { HealthGradesDisclaimer } from './Healthgrades';

type Radio = {
  label: String | ReactNode;
  value: string | number | boolean;
  dnt_label: string;
};

interface Props {
  content?: string;
  bestMatchTitle?: string;
  smartChoiceTitle?: string;
  groupLabel?: string;
  options: Array<Radio>;
  defaultValue?: string | number | boolean;
  model: string;
  submitBtnName?: string;
  disableSubmitBtn?: boolean;
  onOptionChange?: (any) => void;
  onApply?: (value: string) => void;
  labelFontSize?: string;
  locationForAnalytics?: string;
  id?: string;
  isDisabled?: boolean;
}

export const FilterRadioGroup = ({
  content,
  bestMatchTitle,
  smartChoiceTitle,
  groupLabel,
  options,
  defaultValue,
  model,
  submitBtnName,
  disableSubmitBtn = false,
  onOptionChange = () => {},
  onApply = () => {},
  labelFontSize = '16px',
  locationForAnalytics,
  id,
  isDisabled = false,
}: Props) => {
  const { t } = useTranslation();

  const mobileScreen = useMediaQuery(phoneOnly);
  const [radioValue, setRadioValue] = useState(
    defaultValue &&
      options.filter((option) => option.value === defaultValue).length > 0
      ? String(defaultValue)
      : ''
  );

  const suppressDisclaimer = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );

  const buttonStyles: any = {
    'abyss-button-root': {
      height: '42px',
      marginTop: '20px',
      border: 'none',
      borderRadius: '24px',
      width: mobileScreen ? '100%' : '',
      fontWeight: 600,
    },
  };

  useEffect(() => {
    setRadioValue(
      defaultValue &&
        options.filter((option) => option.value === defaultValue).length > 0
        ? String(defaultValue)
        : ''
    );
  }, [defaultValue]);

  // Filter search state
  const [searchTerm, setSearchTerm] = useState('');
  // Capture the initial default value at mount time for reordering (pre-selected on open)
  const initialDefaultValue = useRef<string>(
    defaultValue && options.some((option) => option.value === defaultValue)
      ? String(defaultValue)
      : ''
  );
  // Snapshot of selected value at the time the search term changes
  const preSelectedSnapshot = useRef<string>(
    defaultValue && options.some((option) => option.value === defaultValue)
      ? String(defaultValue)
      : ''
  );
  const [enableVirtualCareFilterAnalytics] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_ANALYTICS_V2,
  ]);

  const { showSearchInput, getNoResultsMessage } = useFilterSearchConfig(
    options.length
  );

  // Update the pre-selected snapshot whenever search term changes
  useEffect(() => {
    if (showSearchInput) {
      preSelectedSnapshot.current = radioValue;
    }
  }, [searchTerm]);

  // Filter options based on search term, keeping pre-selected option at top
  const filteredOptions = useMemo(() => {
    if (!showSearchInput) {
      return options;
    }

    // Use snapshot for search, initial value for non-search reordering
    const pinnedValue = searchTerm.trim()
      ? preSelectedSnapshot.current
      : initialDefaultValue.current;

    // Separate the pre-selected option from the rest
    const selected: typeof options = [];
    const rest: typeof options = [];

    options.forEach((option) => {
      if (pinnedValue && option.value.toString() === pinnedValue) {
        selected.push(option);
      } else {
        rest.push(option);
      }
    });

    // If searching, filter the non-selected items by search term
    const filteredRest = searchTerm.trim()
      ? rest.filter((option) => {
          const label =
            typeof option.label === 'string'
              ? option.label
              : option.dnt_label || '';
          return label.toLowerCase().includes(searchTerm.toLowerCase());
        })
      : rest;

    return [...selected, ...filteredRest];
  }, [options, searchTerm, showSearchInput]);

  // TODO: This data will be collated later for sending to API
  const onSubmit = () => {
    onApply(radioValue);
  };

  const relevanceLabel = t('SORT_OPTIONS.BH_PREFERRED_PROVIDERS');

  function onChangeRadioGroupOption(val) {
    if (val) {
      const option = options.filter(
        (option) => val === option.value.toString()
      )?.[0];

      if (
        model !== FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
        locationForAnalytics !== 'modal:In-person/virtual filter' ||
        enableVirtualCareFilterAnalytics
      ) {
        adobeLinkTrackEvent({
          name: option?.dnt_label,
          location: locationForAnalytics,
          type: 'radio',
        });
      }
    }

    setRadioValue(val);
    onOptionChange({
      [model]: options.filter((option) => val === option.value.toString()),
    });
  }
  const infoContent = () =>
    ((content && bestMatchTitle) || smartChoiceTitle) && (
      <Flex
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '8px',
          fontSize: '16px',
          lineHeight: '20px',
          paddingTop: '16px',
          width: '270px',
        }}
      >
        <Distance
          bestMatchTitle={bestMatchTitle}
          content={content}
          relevanceLabel={relevanceLabel}
          smartChoiceTitle={smartChoiceTitle}
          tabIndex={0}
          title={bestMatchTitle}
          widths={297}
        />
      </Flex>
    );
  return (
    <React.Fragment>
      {showSearchInput && (
        <FilterSearchInput onChange={setSearchTerm} value={searchTerm} />
      )}
      <Flex
        css={{
          'abyss-flex-root': {
            overflow: 'auto',
            maxHeight:
              'calc(var(--radix-popper-available-height, 70vh) - 220px)',
          },
        }}
        direction="column"
      >
        {filteredOptions.length === 0 && searchTerm ? (
          <FilterSearchNoResults message={getNoResultsMessage(searchTerm)} />
        ) : (
          <RadioGroup
            css={{
              'abyss-radio-group-label': {
                margin: '0',
              },
              'abyss-radio-group-root': {
                border: 'none',
              },
              'abyss-radio-group-container': {
                padding: 0,
              },
            }}
            data-auto-testid="radio-group"
            data-testid={`${model}-group`}
            label={groupLabel}
            onChange={(e: { target: { value: string } }) => {
              onChangeRadioGroupOption(e.target.value);
            }}
            validators={{ required: false }}
            value={radioValue}
          >
            <div aria-label={id} role="radiogroup">
              {filteredOptions.map(({ value, label }: Radio) => (
                <RadioGroup.Radio
                  css={{
                    'abyss-radio-root': {
                      padding: '12px 0px 6px $xs',
                      margin: '0 !important',
                      alignItems: 'center !important',
                      '& > div': {
                        marginRight: '4px',
                      },
                    },
                    'abyss-radio-label': {
                      fontSize: labelFontSize,
                      lineHeight: '20px',
                      fontWeight: '$web.core.fontWeight.uhc2020Sans.regular',
                      color:
                        value.toString() === radioValue
                          ? '$core.color.neutral.90'
                          : '$core.color.neutral.80',
                      '.abyss-text-root': {
                        fontSize: labelFontSize,
                        lineHeight: '20px',
                        color:
                          value.toString() === radioValue
                            ? '$core.color.neutral.90'
                            : '$core.color.neutral.80',
                      },
                    },
                  }}
                  data-testid={`${
                    model === 'SortBy'
                      ? `sortBy-${value
                          .toString()
                          .replace(/ /g, '-')
                          .toLocaleLowerCase()}`
                      : model
                  }-radio`}
                  isDisabled={isDisabled}
                  key={value.toString()}
                  label={label}
                  value={value.toString()}
                />
              ))}
            </div>
          </RadioGroup>
        )}
      </Flex>
      {model === FilterCollectionModel.PATIENT_REVIEWS &&
        !suppressDisclaimer && <HealthGradesDisclaimer />}
      {infoContent()}
      {model === FilterCollectionModel.DISTANCE ? <DistanceInfoIcon /> : null}
      {submitBtnName ? (
        <Button
          css={buttonStyles}
          data-auto-testid="apply-button"
          data-testid={`${model}-apply-button`}
          isDisabled={disableSubmitBtn}
          onClick={onSubmit}
        >
          {submitBtnName}
        </Button>
      ) : null}
    </React.Fragment>
  );
};
